"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
var MissingComponent_1 = require("./MissingComponent");
exports.getPlaceholderDataFromRenderingData = function (rendering, name) {
    var result;
    if (rendering && rendering.placeholders && Object.keys(rendering.placeholders).length > 0) {
        result = rendering.placeholders[name];
    }
    else {
        result = null;
    }
    if (!result) {
        console.warn("Placeholder '" + name + "' was not found in the current rendering data", JSON.stringify(rendering, null, 2));
        return [];
    }
    return result;
};
/**
 * Gets Vue elements (VNodes) from placeholder data, using componentFactory to resolve component definitions
 * and createVueElement to create VNodes for use in a Vue `render` function. Note: you can't use VNodes in
 * component templates. You'll need to convert the VNodes to "renderable" components, a.k.a. dynamic components.
 * @see convertVNodesToDynamicComponents or @see getDynamicComponentsFromRenderingData for options.
 * @param {Array<ComponentRendering | HtmlElementRendering>} placeholderData
 * @param {PlaceholderProps} placeholderProps
 * @param {CreateElement} createVueElement
 * @param {ComponentFactory} [componentFactory]
 * @returns {VNode[]} vnodes
 */
function getVNodesForRenderingData(placeholderData, placeholderProps, createVueElement, componentFactory) {
    var placeholderName = placeholderProps.name, placeholderFields = placeholderProps.fields, placeholderParams = placeholderProps.params, missingComponentComponent = placeholderProps.missingComponentComponent, unmappedPlaceholderProps = __rest(placeholderProps, ["name", "fields", "params", "missingComponentComponent"]);
    return placeholderData
        .map(function (rendering, index) {
        var key = rendering.uid ? rendering.uid : "component-" + index;
        // if the element is not a 'component rendering', render it 'raw'
        if (!rendering.componentName && rendering.name) {
            return createRawElement(rendering, createVueElement);
        }
        var component = getComponentForRendering(rendering, componentFactory);
        if (!component) {
            console.error("Placeholder " + placeholderName + " contains unknown component " + rendering.componentName + ". Ensure that a Vue component exists for it, and that it is mapped in your component factory.");
            component = missingComponentComponent || MissingComponent_1.MissingComponent;
        }
        var finalProps = __assign(__assign({}, unmappedPlaceholderProps), { rendering: rendering });
        if (placeholderFields || rendering.fields) {
            finalProps.fields = __assign(__assign({}, placeholderFields), rendering.fields);
        }
        if (placeholderParams || rendering.params) {
            finalProps.params = __assign(__assign({}, placeholderParams), rendering.params);
        }
        return createVueElement(component, { props: finalProps, key: key });
    })
        .filter(function (element) { return element; }); // remove nulls;
}
exports.getVNodesForRenderingData = getVNodesForRenderingData;
/**
 * Convenience method that calls {@link getVNodesForRenderingData} and {@link convertVNodesToDynamicComponents}
 * to return "renderable" components, i.e. components that can be rendered in a Vue template, a.k.a. dynamic components.
 * @param {Array<ComponentRendering | HtmlElementRendering>} placeholderData
 * @param {PlaceholderProps} placeholderProps
 * @param {CreateElement} createVueElement
 * @param {ComponentFactory} componentFactory
 * @returns {JssDynamicComponent[]} dynamic components
 */
function getDynamicComponentsFromRenderingData(placeholderData, placeholderProps, createVueElement, componentFactory) {
    return convertVNodesToDynamicComponents(getVNodesForRenderingData(placeholderData, placeholderProps, createVueElement, componentFactory));
}
exports.getDynamicComponentsFromRenderingData = getDynamicComponentsFromRenderingData;
/**
 * Converts VNodes to simple functional components that render the vnode.
 * Also evaluates VNodes to determine if they are Experience Editor components/elements and if so,
 * adds an identifying property to the component.
 * @param {VNode[]} vnodes
 * @returns {JssDynamicComponent[]} dynamic components
 */
function convertVNodesToDynamicComponents(vnodes) {
    return vnodes.map(function (vnode) {
        var component = {
            functional: true,
            props: vnode.data && vnode.data.props,
            render: function () {
                return vnode;
            },
        };
        if (vnode.tag === 'code' &&
            vnode.data &&
            vnode.data.attrs &&
            vnode.data.attrs.type === 'text/sitecore') {
            component.isxEditorComponent = true;
        }
        return component;
    });
}
exports.convertVNodesToDynamicComponents = convertVNodesToDynamicComponents;
/**
 * @param {any} elem
 * @param {CreateElement} createVueElement
 */
function createRawElement(elem, createVueElement) {
    if (!elem.name) {
        console.error('"elem.name" is undefined in "createRawElement". ' +
            'Something is likely wrong with your component data. Ensure that your components have a name.');
        return null;
    }
    var attrs = elem.attributes;
    var domProps = {
        innerHTML: elem.contents,
    };
    var component = createVueElement(elem.name, { attrs: attrs, domProps: domProps });
    return component;
}
/**
 * @param {Object} renderingDefinition
 * @param {string} renderingDefinition.componentName
 * @param {ComponentFactory} [componentFactory]
 */
function getComponentForRendering(renderingDefinition, componentFactory) {
    if (!componentFactory) {
        console.warn("No componentFactory was available to service request for component " + renderingDefinition.componentName);
        return null;
    }
    var component = componentFactory(renderingDefinition.componentName);
    return component;
}
